import {Trans} from '@lingui/macro';
import React, {ReactElement} from 'react';
import styled from 'styled-components';

import {useUserTheme} from '../../../../../libs/shared/ui/src/theme/userThemeProvider';
import {SignatureModel} from '../../../../models/api/signatureModel';
import {BoxedIcon} from '../../boxedIcon';
import {
  getCellBackgroundColor,
  getCellIndexStyles,
  getCellTextColor,
  getTableCellAvatarSize,
} from '../helpers/tableCellHelpers';
import {getWidthSettingsStyles} from '../tableColumn';
import {StyledBlurbDiv, StyledDetailsContainerDiv, StyledNameDiv} from './settingsCommonCellStyles';
import {TableCell, TableCellPropsWithAvatar, TableCellStyleProps} from './tableCell';

/*
 * Styles.
 */

interface StyledSignatureDivProps {
  $avatarSize: number;
}

const StyledSignatureIconContainerDiv = styled.div<StyledSignatureDivProps>`
  grid-area: icon;
`;

const StyledSignatureCellContainerDiv = styled.div<TableCellStyleProps>`
  padding: 0 12px;
  overflow: hidden;
  white-space: nowrap;
  display: grid;
  flex: 1;
  height: 100%;
  align-items: center;

  grid-template-areas: 'icon . label .';
  grid-template-columns: auto 12px 1fr 7px;

  ${(p) => getCellBackgroundColor(p.$isHovering, p.$isSelected)}
  ${(p) => getWidthSettingsStyles(p.$settings?.cellWidth, p.$index)}
  ${(p) =>
    getCellIndexStyles(
      p.$index,
      p.$isSelected,
      p.$shouldRemoveTopBorderRadius,
      p.$shouldRemoveBottomBorderRadius,
    )}
  ${(p) => getCellTextColor(p.$isSelected)}
`;

const StyledDefaultSignatureNameDiv = styled.div`
  font-style: italic;
`;

/*
 * Component.
 */

export const SignatureCell = (props: TableCellPropsWithAvatar<SignatureModel>): ReactElement => {
  const theme = useUserTheme();
  const {
    isSelected,
    isHovering,
    isLoading,
    size,
    settings,
    index,
    avatarSize,
    shouldShowStickyGradient,
    shouldRemoveTopBorderRadius,
    shouldRemoveBottomBorderRadius,
  } = props;

  const avatarSizePx = getTableCellAvatarSize(size, avatarSize);

  const renderName = (value: SignatureModel) => {
    const hasNameOrSenderInfo = Boolean(value.name) || Boolean(value.senderInfo);
    if (!hasNameOrSenderInfo) {
      return <StyledDefaultSignatureNameDiv>{defaultSignatureName}</StyledDefaultSignatureNameDiv>;
    }
    return <StyledNameDiv>{value.name || value.senderInfo}</StyledNameDiv>;
  };

  return (
    <TableCell<SignatureModel>
      isSelected={isSelected}
      isHovering={isHovering}
      isLoading={isLoading}
      size={size}
      settings={settings}
      index={index}
      value={props.value}
      shouldShowStickyGradient={shouldShowStickyGradient}
      shouldRemoveTopBorderRadius={shouldRemoveTopBorderRadius}
      shouldRemoveBottomBorderRadius={shouldRemoveBottomBorderRadius}
      renderCell={(value) => (
        <StyledSignatureCellContainerDiv
          $settings={settings}
          $isHovering={Boolean(isHovering)}
          $index={index}
          $isSelected={Boolean(isSelected)}
          $shouldRemoveTopBorderRadius={shouldRemoveTopBorderRadius}
          $shouldRemoveBottomBorderRadius={shouldRemoveBottomBorderRadius}
        >
          <StyledSignatureIconContainerDiv $avatarSize={avatarSizePx}>
            <BoxedIcon
              name="featherSmall"
              color={theme.greys.shade70}
              borderColor={theme.greys.shade30}
              borderStyle="solid"
              size={size}
            />
          </StyledSignatureIconContainerDiv>
          <StyledDetailsContainerDiv>
            {renderName(value)}
            <StyledBlurbDiv $isSelected={Boolean(isSelected)}>{value.blurb}</StyledBlurbDiv>
          </StyledDetailsContainerDiv>
        </StyledSignatureCellContainerDiv>
      )}
    />
  );
};

const defaultSignatureName = <Trans id="settings-no-signature-name">No sender info</Trans>;
